import { useEffect, useState } from 'react';
import {
  RiAlertLine,
  RiBuilding2Line,
  RiExternalLinkLine,
  RiMapPinLine,
  RiPriceTag3Line,
  RiShieldCheckLine,
  RiUserStarLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { api } from '../state/api';
import { useStore } from '../state/store';
import { useCanvasStore } from '../canvas/canvasStore';
import { entityIcon, labelForType, RISK_BG, RISK_LABEL } from './icons';
import { PRIORITY_LABEL, TARGET_STATUS_LABEL } from '../state/domain';
import type { RiskFactor } from '../state/domain';
import { cn, formatDate, initials } from '@/lib/utils';

export function DossierPanel() {
  const activeTargetId = useStore(state => state.activeTargetId);
  const targets = useStore(state => state.targets);
  const entities = useStore(state => state.entities);
  const evidence = useStore(state => state.evidence);
  const timeline = useStore(state => state.timeline);
  const selectTarget = useStore(state => state.selectTarget);
  const controller = useCanvasStore(state => state.controller);

  const target = targets.find(t => t.id === activeTargetId);
  const [factors, setFactors] = useState<RiskFactor[]>([]);

  useEffect(() => {
    if (!activeTargetId) {
      setFactors([]);
      return;
    }
    let alive = true;
    api
      .targetRisk(activeTargetId)
      .then(res => {
        if (alive) setFactors(res.factors ?? []);
      })
      .catch(() => alive && setFactors([]));
    return () => {
      alive = false;
    };
  }, [activeTargetId, target?.updatedAt]);

  if (!target) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">对象档案</span>
        <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground">
          在左侧「对象」中选择一个对象以查看完整档案。
        </div>
      </div>
    );
  }

  const clues = entities.filter(e => e.targetId === target.id);
  const targetEvidence = evidence.filter(e => e.targetId === target.id);
  const events = timeline.filter(e => e.targetId === target.id || e.participants.includes(target.id));

  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 border-b border-border p-4">
        <div className="size-14 shrink-0 overflow-hidden rounded-[var(--radius)] border border-border bg-muted">
          {target.photo ? (
            <img src={target.photo} alt="" className="size-full object-cover" />
          ) : (
            <div className="grid size-full place-items-center bg-primary text-[15px] font-semibold text-primary-foreground">
              {initials(target.name)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {target.kind === 'organization' ? (
              <RiBuilding2Line className="size-3.5 text-muted-foreground" />
            ) : (
              <RiUserStarLine className="size-3.5 text-muted-foreground" />
            )}
            <h2 className="truncate text-[15px] font-semibold leading-tight">{target.name}</h2>
          </div>
          {target.codename && (
            <div className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">代号 {target.codename}</div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={cn('border-transparent', RISK_BG[target.riskLevel])}>
              <RiAlertLine className="size-3" />
              风险 {target.riskScore} · {RISK_LABEL[target.riskLevel]}
            </Badge>
            <Badge variant="muted">{target.kind === 'organization' ? '组织' : '个人'}</Badge>
            <Badge variant={target.status === 'watch' ? 'cord' : 'outline'}>{TARGET_STATUS_LABEL[target.status]}</Badge>
            {target.priority !== 'normal' && (
              <Badge variant="cord">优先级 {PRIORITY_LABEL[target.priority]}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <Field label="职务 / 角色" value={target.role} />
          <Field label="所属组织" value={target.org} />
          <Field
            label="所在地"
            value={target.location}
            icon={target.location ? <RiMapPinLine className="size-3" /> : undefined}
          />
          <Field label="国籍" value={target.nationality} />
          <Field label="出生日期" value={target.dob} />
          <Field label="画像置信度" value={`${Math.round(target.confidence * 100)}%`} />
          <Field label="最后出现" value={formatDate(target.lastSeenAt)} />
          <Field label="建档时间" value={formatDate(target.createdAt)} />
        </div>

        {(target.aliases.length > 0 || target.tags.length > 0 || target.affiliations.length > 0) && (
          <div className="flex flex-col gap-1.5">
            <span className="label-caps">别名 / 机构 / 标签</span>
            <div className="flex flex-wrap gap-1">
              {target.aliases.map(alias => (
                <Badge key={alias} variant="muted">
                  {alias}
                </Badge>
              ))}
              {target.affiliations.map(item => (
                <Badge key={item} variant="lead">
                  {item}
                </Badge>
              ))}
              {target.tags.map(tag => (
                <Badge key={tag} variant="outline">
                  <RiPriceTag3Line className="size-3" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {Object.keys(target.idNumbers).length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="label-caps">证件 / 编号</span>
            <div className="overflow-hidden rounded-[var(--radius-sm)] border border-border">
              {Object.entries(target.idNumbers).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between border-b border-border px-2.5 py-1.5 text-[11px] last:border-b-0">
                  <span className="text-muted-foreground">{key}</span>
                  <span className="font-mono">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {target.summary && (
          <div className="flex flex-col gap-1.5">
            <span className="label-caps">背景备注</span>
            <p className="rounded-[var(--radius)] border border-border bg-muted/40 p-2.5 text-[11.5px] leading-relaxed">
              {target.summary}
            </p>
          </div>
        )}

        <Separator />

        <div className="flex flex-col gap-2">
          <span className="label-caps">风险因子（{factors.length}）</span>
          {factors.length === 0 && (
            <p className="text-[11px] text-muted-foreground">暂无因子，采集线索后自动计算。</p>
          )}
          {factors.map(factor => (
            <div key={factor.key} className="rounded-[var(--radius-sm)] border border-border bg-card p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11.5px] font-medium">{factor.label}</span>
                <span className="font-mono text-[10px] text-[var(--color-cord)]">
                  +{Math.round(factor.score * factor.weight)}
                </span>
              </div>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{factor.rationale}</p>
            </div>
          ))}
        </div>

        <Separator />

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="label-caps">关联线索（{clues.length}）</span>
            {clues.length > 0 && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  if (!controller) return;
                  controller.addEntityNode(controller.viewport.center, {
                    title: target.name,
                    text: clues.map(c => `${labelForType(c.type)}：${c.value ?? c.label}`).join('\n'),
                    color: 'blue',
                    refKind: 'target',
                    refId: target.id,
                  });
                }}
              >
                汇总上板
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-border overflow-hidden rounded-[var(--radius)] border border-border">
            {clues.slice(0, 30).map(clue => {
              const Icon = entityIcon(clue.type);
              return (
                <div key={clue.id} className="flex items-center gap-2 px-2.5 py-1.5">
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="w-14 shrink-0 text-[10px] text-muted-foreground">{labelForType(clue.type)}</span>
                  <span className="truncate font-mono text-[11px]">{clue.value ?? clue.label}</span>
                  <span className="ml-auto shrink-0 font-mono text-[9.5px] text-muted-foreground">
                    {(clue.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              );
            })}
            {clues.length === 0 && <div className="p-2.5 text-[11px] text-muted-foreground">无线索</div>}
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="label-caps">相关时间线（{events.length}）</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {events.slice(0, 6).map(event => (
              <div key={event.id} className="flex gap-2 rounded-[var(--radius-sm)] border border-border bg-card p-2">
                <span className="mt-1 size-2 shrink-0 rounded-full bg-[var(--color-assess)]" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[11.5px] font-medium">{event.title}</span>
                    <span className="ml-auto shrink-0 font-mono text-[9.5px] text-muted-foreground">
                      {formatDate(event.occurredAt)}
                    </span>
                  </div>
                  {event.description && (
                    <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">{event.description}</p>
                  )}
                </div>
              </div>
            ))}
            {events.length === 0 && <span className="text-[11px] text-muted-foreground">暂无相关事件</span>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
          <RiShieldCheckLine className="size-3.5 text-[var(--color-evidence)]" />
          已归档证据 {targetEvidence.length} 条
        </div>

        <Button variant="outline" size="sm" className="self-start" onClick={() => selectTarget(target.id)}>
          <RiExternalLinkLine className="size-3.5" />
          在台账中定位
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | null | undefined;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1 truncate text-[11.5px]">{icon}{value ?? '—'}</span>
    </div>
  );
}